
import {
  defineComponent, PropType, computed, ref, nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import type { OwnerIntegrationInfo } from '@/types/integrations';

export interface EventPickerItem {
  label: string;
  value: string;
  owner_integration?: OwnerIntegrationInfo | null;
}

/**
 * Per-integration bucket used for the left-rail nested filter.
 */
interface IntegrationBucket {
  idIntegration: number;
  name: string;
  iconUrl: string | null;
  items: EventPickerItem[];
}

/**
 * The current selection in the left rail. `kind` discriminates:
 *   - 'all'              → show everything
 *   - 'predefined'       → only ShopiMind natives
 *   - 'custom'           → only owner-less customs
 *   - 'integrations-all' → all integration-owned events (any integration)
 *   - 'integration'      → events of a specific integration (idIntegration)
 */
type ActiveFilter =
  | { kind: 'all' }
  | { kind: 'predefined' }
  | { kind: 'custom' }
  | { kind: 'integrations-all' }
  | { kind: 'integration'; idIntegration: number };

export default defineComponent({
  name: 'EventPickerDialog',

  components: {
    Dialog,
    InputText,
    Button,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: String,
      default: '',
    },

    predefinedItems: {
      type: Array as PropType<EventPickerItem[]>,
      default: () => [],
    },

    customItems: {
      type: Array as PropType<EventPickerItem[]>,
      default: () => [],
    },
  },

  emits: ['update:visible', 'update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const searchQuery = ref('');
    const searchInputRef = ref();
    const activeFilter = ref<ActiveFilter>({ kind: 'all' });

    const visibleLocal = computed({
      get: () => props.visible,
      set: (val: boolean) => emit('update:visible', val),
    });

    // ----------------------------------------------------------------------
    // Bucketise customItems once per source change. The left rail's per-
    // integration sub-list and the counts both read from this.
    // ----------------------------------------------------------------------

    const integrationBuckets = computed<IntegrationBucket[]>(() => {
      const map = new Map<number, IntegrationBucket>();
      for (const item of props.customItems) {
        const o = item.owner_integration;
        if (!o?.id_integration) continue;
        let bucket = map.get(o.id_integration);
        if (!bucket) {
          bucket = {
            idIntegration: o.id_integration,
            name: o.name || o.slug || `Integration ${o.id_integration}`,
            iconUrl: o.icon_url ?? null,
            items: [],
          };
          map.set(o.id_integration, bucket);
        }
        bucket.items.push(item);
      }
      // Stable order by name.
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    });

    const customOnly = computed(() => props.customItems.filter(
      (i) => !i.owner_integration?.id_integration,
    ));
    const integrationOnly = computed(() => props.customItems.filter(
      (i) => !!i.owner_integration?.id_integration,
    ));

    const predefinedCount = computed(() => props.predefinedItems.length);
    const customCount = computed(() => customOnly.value.length);
    const integrationTotalCount = computed(() => integrationOnly.value.length);
    const totalEventCount = computed(
      () => predefinedCount.value + customCount.value + integrationTotalCount.value,
    );

    // ----------------------------------------------------------------------
    // The flat list shown on the right, derived from the active filter +
    // the search query.
    // ----------------------------------------------------------------------

    const filteredBase = computed<EventPickerItem[]>(() => {
      const f = activeFilter.value;
      switch (f.kind) {
        case 'all':
          // Order in "All": predefined first (most common), then by
          // integration alphabetical, then customs. Matches the
          // discoverability hierarchy.
          return [
            ...props.predefinedItems,
            ...integrationOnly.value,
            ...customOnly.value,
          ];
        case 'predefined':
          return props.predefinedItems;
        case 'custom':
          return customOnly.value;
        case 'integrations-all':
          return integrationOnly.value;
        case 'integration': {
          const bucket = integrationBuckets.value.find(
            (b) => b.idIntegration === f.idIntegration,
          );
          return bucket?.items ?? [];
        }
        default:
          return [];
      }
    });

    const visibleItems = computed<EventPickerItem[]>(() => {
      const q = searchQuery.value.toLowerCase().trim();
      if (!q) return filteredBase.value;
      return filteredBase.value.filter(
        (item) => item.label.toLowerCase().includes(q)
          || item.value.toLowerCase().includes(q),
      );
    });

    // ----------------------------------------------------------------------
    // Behaviour
    // ----------------------------------------------------------------------

    const setFilter = (next: ActiveFilter) => {
      activeFilter.value = next;
    };

    const onShow = () => {
      searchQuery.value = '';
      activeFilter.value = { kind: 'all' };
      nextTick(() => {
        searchInputRef.value?.$el?.focus();
      });
    };

    const selectEvent = (value: string) => {
      emit('update:modelValue', value);
      visibleLocal.value = false;
    };

    return {
      t,
      visibleLocal,
      searchQuery,
      searchInputRef,
      activeFilter,
      setFilter,
      integrationBuckets,
      predefinedCount,
      customCount,
      integrationTotalCount,
      totalEventCount,
      visibleItems,
      onShow,
      selectEvent,
    };
  },
});
